import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Box, Paper, Toolbar, Typography, Stack, TextField, Button } from "@mui/material";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import axios from "axios";
import { DataGrid } from "@mui/x-data-grid";
import Swal from "sweetalert2";

export default function ServiceRequest() {
  const [serviceTypes, setServiceTypes] = useState([]);
  const [serviceCharges, setServiceCharges] = useState([]);
  const [acyear, setAcyear] = useState({ acyear_id: "", acyear_name: "" });
  const [yearSem, setYearSem] = useState({ year_number: "", semester_number: "" });

  const [form, setForm] = useState({
    college_id: "",
    admittedbatch_id: "",
    acyear_id: "",
    student_id: "",
    student_rollno: "",
    service_type_id: "",
    service_charge_id: "",
    service_charge: "",
    purpose: "",
    request_date: "",
    process_date: "",
    year_number: "",
    semester_number: "",
    fee_head_id: "",
    paid_status: "UNPAID",
  });

  const isStudyCertificate = useMemo(() => {
    const selected = serviceTypes.find(
      (t) => String(t.service_type_id) === String(form.service_type_id)
    );
    return selected && selected.service_type_name?.toLowerCase() === "study certificate";
  }, [form.service_type_id, serviceTypes]);
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const user = useMemo(() => {
    const u = sessionStorage.getItem("user");
    return u ? JSON.parse(u) : null;
  }, []);

  const getAuthHeaders = () => {
    const token = sessionStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const showSnackbar = (msg, sev = "success") => {
    setSnackbarMessage(msg);
    setSnackbarSeverity(sev);
    setSnackbarOpen(true);
  };

  useEffect(() => {
    if (!user) return;
    const fetchStudent = async () => {
      try {
        const url = `${API_BASE_URL}/api/students/by-email/${user.client_id}/${encodeURIComponent(
          user.user_email
        )}?college_id=${encodeURIComponent(user.college_id || "")}`;
        const res = await axios.get(url, {
          headers: getAuthHeaders(),
        });
        const data = res.data?.data || res.data || null;
        if (data && data.student_id) {
          setForm((prev) => ({
            ...prev,
            student_id: data.student_id,
            student_rollno: data.student_rollno || "",
            college_id: data.college_id || user.college_id || "",
            admittedbatch_id: data.admittedbatch_id || user.admittedbatch_id || "",
          }));
        }
      } catch (err) {}
    };
    const fetchAcademicYear = async () => {
      try {
        const url = `${API_BASE_URL}/api/acyear/by-college/${user.client_id}/${user.college_id}`;
        const res = await axios.get(url, {
          headers: getAuthHeaders(),
        });
        const data = res.data?.data || res.data || null;
        if (data && data.acyear_id) {
          setForm((prev) => ({ ...prev, acyear_id: data.acyear_id }));
        }
      } catch (err) {}
    };
    fetchStudent();
    fetchAcademicYear();
  }, [user, API_BASE_URL]);

  const fetchServiceTypes = useCallback(async () => {
    if (!user) return;
    try {
      const url = `${API_BASE_URL}/api/service-types/${user.client_id}/${user.college_id}`;
      const res = await axios.get(url, {
        headers: getAuthHeaders(),
      });
      const data = Array.isArray(res.data) ? res.data : res.data?.data || [];
      setServiceTypes(data);
    } catch (err) {
      setServiceTypes([]);
    }
  }, [user, API_BASE_URL]);

  const fetchServiceCharges = useCallback(
    async (serviceTypeId) => {
      if (!user || !serviceTypeId) {
        setServiceCharges([]);
        return;
      }
      try {
        const url = `${API_BASE_URL}/api/service-charges/${user.client_id}/${user.college_id}?service_type_id=${serviceTypeId}`;
        const res = await axios.get(url, {
          headers: getAuthHeaders(),
        });
        const data = Array.isArray(res.data) ? res.data : res.data?.data || [];
        setServiceCharges(data);
      } catch (err) {
        setServiceCharges([]);
      }
    },
    [user, API_BASE_URL]
  );

  const fetchAcyearForAdmittedBatch = useCallback(async () => {
    if (!user || !form.admittedbatch_id) return;
    try {
      const url = `${API_BASE_URL}/api/acyear/by-admittedbatch/${user.client_id}/${user.college_id}/${form.admittedbatch_id}`;
      const res = await axios.get(url, {
        headers: getAuthHeaders(),
      });
      const data = res.data?.data || res.data || null;
      if (data) {
        setAcyear({ acyear_id: data.acyear_id || "", acyear_name: data.acyear_name || "" });
        setForm((prev) => ({ ...prev, acyear_id: data.acyear_id || "" }));
      }
    } catch (err) {}
  }, [user, API_BASE_URL, form.admittedbatch_id]);

  const fetchYearSemester = useCallback(async () => {
    if (!user || !form.admittedbatch_id) return;
    try {
      const url = `${API_BASE_URL}/api/program-year-semester/${user.client_id}/${user.college_id}?admittedbatch_id=${form.admittedbatch_id}`;
      const res = await axios.get(url, {
        headers: getAuthHeaders(),
      });
      const data = res.data?.data || res.data || null;
      if (data) {
        const yn = data.year_number ?? data.yearno ?? "";
        const sn = data.semester_number ?? data.semno ?? "";
        setYearSem({ year_number: yn, semester_number: sn });
        setForm((prev) => ({ ...prev, year_number: yn, semester_number: sn }));
      }
    } catch (err) {}
  }, [user, API_BASE_URL, form.admittedbatch_id]);

  // initial loads
  useEffect(() => {
    if (user) fetchServiceTypes();
  }, [user, fetchServiceTypes]);

  useEffect(() => {
    if (user && form.admittedbatch_id) {
      fetchAcyearForAdmittedBatch();
      fetchYearSemester();
    }
  }, [user, form.admittedbatch_id, fetchAcyearForAdmittedBatch, fetchYearSemester]);

  // When service type changes -> refresh charges and reset selection
  useEffect(() => {
    if (form.service_type_id) {
      fetchServiceCharges(form.service_type_id);
      setForm((prev) => ({ ...prev, service_charge_id: "", service_charge: "" }));
    } else {
      setServiceCharges([]);
      setForm((prev) => ({ ...prev, service_charge_id: "", service_charge: "" }));
    }
  }, [form.service_type_id, fetchServiceCharges]);

  // When charge id changes -> pick amount automatically from tbl_service_charges
  useEffect(() => {
    if (!form.service_charge_id) return;
    const sel = serviceCharges.find(
      (c) => String(c.service_charge_id) === String(form.service_charge_id)
    );
    if (sel) {
      setForm((prev) => ({ ...prev, service_charge: sel.service_charge ?? "" }));
    }
  }, [form.service_charge_id, serviceCharges]);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  // Fetch service requests for the student
  const fetchServiceRequests = useCallback(async () => {
    if (!form.student_id || !form.college_id) return;
    setLoading(true);
    try {
      const url = `${API_BASE_URL}/api/servicerequest/list?student_id=${
        form.student_id
      }&client_id=${user?.client_id || ""}`;
      const res = await axios.get(url, {
        headers: getAuthHeaders(),
      });
      setRows(res.data?.data || []);
    } catch (err) {
      setRows([]);
    }
    setLoading(false);
  }, [form.student_id, form.college_id, user, API_BASE_URL]);

  // Fetch on student_id change
  useEffect(() => {
    if (form.student_id) fetchServiceRequests();
  }, [form.student_id, fetchServiceRequests]);

  // When saving request
  const handleSubmit = async () => {
    if (!form.service_type_id) return showSnackbar("Please select a Service Type", "error");
    if (!form.service_charge_id) return showSnackbar("Please select a Service Charge", "error");
    try {
      const payload = {
        student_id: form.student_id ? Number(form.student_id) : null,
        service_type_id: Number(form.service_type_id),
        service_charge_id: Number(form.service_charge_id),
        service_charge: form.service_charge ? Number(form.service_charge) : null,
        purpose: form.purpose || null,
        acyear_id: form.acyear_id ? Number(form.acyear_id) : null,
        year_number: form.year_number ? String(form.year_number) : null,
        semester_number: form.semester_number ? String(form.semester_number) : null,
        fee_head_id: form.fee_head_id || null,
        paid_status: (form.paid_status || "UNPAID").toUpperCase(),
        email: user?.user_email || null,
        client_id: user?.client_id || null,
      };
      await axios.post(`${API_BASE_URL}/api/servicerequest/student`, payload, {
        headers: getAuthHeaders(),
      });
      showSnackbar("Service request saved", "success");
      fetchServiceRequests(); // refresh grid
    } catch (err) {
      showSnackbar(err?.response?.data?.message || "Error saving service request", "error");
    }
  };

  // Delete handler
  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "Are you sure you want to delete the request?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    });
    if (!result.isConfirmed) return;
    try {
      const res = await axios.delete(
        `${API_BASE_URL}/api/servicerequest/${id}?client_id=${user?.client_id || ""}`,
        { headers: getAuthHeaders() }
      );
      showSnackbar("Request deleted", "success");
      fetchServiceRequests();
    } catch (err) {
      if (err?.response?.data?.message?.includes("PAID")) {
        await Swal.fire({
          icon: "warning",
          title: "Cannot delete",
          text: "Cannot delete: status is PAID",
          confirmButtonColor: "#1976d2",
        });
      } else {
        showSnackbar("Failed to delete request", "error");
      }
    }
  };

  // DataGrid columns
  const columns = [
    { field: "service_type_name", headerName: "Service Type", flex: 1, minWidth: 120 },
    { field: "service_charge", headerName: "Service Charge", flex: 1, minWidth: 120 },
    {
      field: "amount",
      headerName: "Amount",
      flex: 1,
      minWidth: 100,
      valueGetter: (params) => params.row.service_charge,
    },
    { field: "paid_status", headerName: "Paid Status", flex: 1, minWidth: 120 },
    {
      field: "purpose",
      headerName: "Purpose",
      flex: 1.2,
      minWidth: 140,
      renderCell: (params) => (params.value ? params.value : "-"),
      hide: rows.every((row) => !row.purpose),
    },
    {
      field: "actions",
      headerName: "Actions",
      minWidth: 100,
      flex: 0.7,
      sortable: false,
      renderCell: (params) => (
        <Button
          color="error"
          size="medium"
          onClick={() => handleDelete(params.row.id || params.row.service_request_id)}
        >
          Delete
        </Button>
      ),
    },
  ];

  return (
    <Box p={2}>
      <Paper elevation={1}>
        <Toolbar sx={{ gap: 1 }}>
          <Typography variant="h5" sx={{ flexGrow: 1 }}>
            STUDENT SERVICE REQUEST
          </Typography>
        </Toolbar>

        <Box p={2}>
          <Stack spacing={2}>
            {/* Service selectors and Save button in one row */}
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="flex-end">
              <TextField
                select
                margin="normal"
                label="Service Type"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={form.service_type_id}
                onChange={(e) => setForm((p) => ({ ...p, service_type_id: e.target.value }))}
                SelectProps={{ native: true }}
              >
                <option value="">Select Service Type</option>
                {serviceTypes.map((t) => (
                  <option key={t.service_type_id} value={t.service_type_id}>
                    {t.service_type_name}
                  </option>
                ))}
              </TextField>

              <TextField
                select
                margin="normal"
                label="Service Charge"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={form.service_charge_id}
                onChange={(e) => setForm((p) => ({ ...p, service_charge_id: e.target.value }))}
                SelectProps={{ native: true }}
              >
                <option value="">Select Service Charge</option>
                {serviceCharges.map((c) => (
                  <option key={c.service_charge_id} value={c.service_charge_id}>
                    {c.service_description || "Charge"}
                  </option>
                ))}
              </TextField>

              <TextField
                label="Amount"
                fullWidth
                value={form.service_charge}
                InputProps={{ readOnly: true }}
              />

              <Button
                variant="contained"
                color="primary"
                onClick={async () => {
                  const result = await Swal.fire({
                    title: "Do you want to send the request?",
                    icon: "question",
                    showCancelButton: true,
                    confirmButtonText: "Yes",
                    cancelButtonText: "No",
                    confirmButtonColor: "#1976d2",
                    cancelButtonColor: "#aaa",
                  });
                  if (result.isConfirmed) {
                    handleSubmit();
                  }
                }}
                sx={{ height: 56, minWidth: 120, fontWeight: "bold", fontSize: 16, color: "#fff" }}
              >
                Save Request
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Paper>

      {/* Service Requests Grid */}
      <Box mt={3}>
        <DataGrid
          rows={rows}
          columns={columns.filter((col) => !col.hide)}
          autoHeight
          loading={loading}
          getRowId={(row) => row.id || row.service_request_id}
          pageSize={5}
          rowsPerPageOptions={[5, 10, 20]}
        />
      </Box>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MuiAlert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
          elevation={6}
          variant="filled"
        >
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </Box>
  );
}
